import { AlternateExerciseSearchParams } from './alternate-exercise-search';
import { STOP_WORDS } from './exercise-search-stopwords';

/**
 * Local re-creation of the server-side weighted exercise search
 * (`searchExercises` in libs/trpc-server/src/lib/exercise-library/router.ts)
 * so the block builder can rank a preloaded exercise list without a
 * per-keystroke query.
 *
 * Field weights mirror the `setweight()` calls in the search_vector trigger
 * (libs/db/schema/src/lib/migrations/functions/exercise_search.sql): name 1.0,
 * tag names 0.2, body part / equipment names 0.1. Postgres `ts_rank` is not
 * reproducible client-side, so text relevance is approximated as the mean of
 * each search word's best field weight; relative ordering (and the name
 * tie-break) matches the server for the common cases.
 */

const NAME_WEIGHT = 1.0;
const TAG_WEIGHT = 0.2;
const META_WEIGHT = 0.1;

// Keep in sync with OVERLAP_TEXT_WEIGHT / OVERLAP_TAG_WEIGHT in the router.
const OVERLAP_TEXT_WEIGHT = 0.7;
const OVERLAP_TAG_WEIGHT = 0.3;

type SearchableExercise = {
  id: string;
  name: string;
  /** Postgres tsvector as text, e.g. `'back':1A 'squat':2A,7C`. */
  searchVector?: string | null;
  tags: { tag: { id: string; name: string } }[];
  bodyParts: { bodyPart: { id: string; name: string } }[];
  equipment: { equipment: { id: string; name: string } }[];
};

/**
 * Same sanitization the server applies before building the tsquery, plus a
 * split on non-alphanumerics: tsquery parses "push-up:*" as 'push':* & 'up':*,
 * so "Push-Up" must be findable by its exact string.
 */
function searchWords(searchTerm: string, locale: string): string[] {
  // NFC so a pasted decomposed accent (e + U+0301) is one character,
  // not a token boundary.
  const stopWords = STOP_WORDS[locale] ?? STOP_WORDS['en'];
  return searchTerm
    .normalize('NFC')
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[&|!():*'<>]/g, ''))
    .filter((word) => word.length > 0 && /[\p{L}\p{N}]/u.test(word))
    .flatMap((word) => word.toLowerCase().split(/[^\p{L}\p{N}]+/u))
    .filter(Boolean)
    // Postgres drops stop words from the tsquery ('clean and jerk' ->
    // 'clean:* & jerk:*'); do the same so they can't exclude matches.
    .filter((word) => !stopWords.has(word));
}

// Unicode-aware to mirror Postgres tokenization, which keeps accented letters
// in tokens rather than splitting on them ("Développé" is one token).
function tokens(text: string): string[] {
  return text
    .normalize('NFC')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/**
 * Crude stand-in for Postgres stemming, used only in the raw-field fallback
 * for records without a search_vector (see wordScore): the server stems both
 * sides via to_tsvector/to_tsquery, so "squats" matches "Back Squat" there.
 * common English suffixes as match candidates. Residual gap: real stemming
 * (e.g. "running" -> "run" needs the doubled consonant restored) and
 * non-English org languages are not reproduced.
 */
function stemCandidates(word: string): string[] {
  // The original word always stays a candidate (even a single character);
  // only the derived stems need a length floor to avoid matching everything.
  return [
    word,
    ...[word.replace(/(es|s)$/, ''), word.replace(/(ing|ed)$/, '')].filter(
      (candidate) => candidate.length >= 2,
    ),
  ];
}

function tokenMatches(word: string, text: string): boolean {
  const textTokens = tokens(text);
  return stemCandidates(word).some((candidate) =>
    textTokens.some((token) => token.startsWith(candidate)),
  );
}

// ts_rank's default class weights (D=0.1, C=0.2, B=0.4, A=1.0) — the
// search_vector trigger assigns A to names, C to tags, D to body parts and
// equipment.
const CLASS_WEIGHTS: Record<string, number> = { A: 1.0, B: 0.4, C: 0.2 };
const DEFAULT_CLASS_WEIGHT = 0.1;

type Lexeme = { text: string; weight: number };

const lexemeCache = new WeakMap<SearchableExercise, Lexeme[]>();

/**
 * Parse a tsvector's text form into lexemes with their best weight class.
 * Entries look like `'lexeme':1A,3C` (positions with optional weight, default
 * D) or a bare `'lexeme'`. Lexemes are server-stemmed in the org's language.
 * Parsed once per record — the cache is keyed on the record object.
 */
function parseSearchVector(exercise: SearchableExercise): Lexeme[] {
  const cached = lexemeCache.get(exercise);
  if (cached) {
    return cached;
  }
  const vector = exercise.searchVector ?? '';
  const lexemes: Lexeme[] = [];
  // 'lexeme' optionally followed by :positions; '' is an escaped quote.
  const entryPattern = /'((?:[^']|'')+)'(?::([\dA-D,]+))?/g;
  let match: RegExpExecArray | null;
  while ((match = entryPattern.exec(vector)) !== null) {
    const text = (match[1] ?? '').replace(/''/g, "'");
    let weight = DEFAULT_CLASS_WEIGHT;
    for (const position of (match[2] ?? '').split(',')) {
      const cls = position.slice(-1);
      const classWeight = CLASS_WEIGHTS[cls];
      if (classWeight !== undefined && classWeight > weight) {
        weight = classWeight;
      }
    }
    lexemes.push({ text, weight });
  }
  lexemeCache.set(exercise, lexemes);
  return lexemes;
}

/**
 * Match against the server's stemmed lexemes. Prefix in both directions:
 * 'sq' finds 'squat' (lexeme starts with word), and 'fente' finds the
 * French-stemmed 'fent' (word starts with lexeme) — per-locale stemming
 * parity without a client-side stemmer. The reverse direction stands in for
 * stemming the query, so it only fires when the lexeme looks like a real
 * stem: long enough on its own, and dropping at most a short suffix —
 * otherwise 1-letter lexemes ('b' from "B-Skip") match every word starting
 * with that letter.
 */
function lexemeScore(word: string, exercise: SearchableExercise): number {
  let best = 0;
  for (const lexeme of parseSearchVector(exercise)) {
    const isStemOfWord =
      lexeme.text.length >= 3 &&
      word.startsWith(lexeme.text) &&
      word.length - lexeme.text.length <= 3;
    if (lexeme.text.startsWith(word) || isStemOfWord) {
      best = Math.max(best, lexeme.weight);
    }
  }
  return best;
}

/**
 * Best field weight for a prefix match of `word`, or 0 when nothing matches.
 * The server requires every search word to match (tsquery `&`), so a 0 here
 * excludes the exercise.
 */
function wordScore(word: string, exercise: SearchableExercise): number {
  // Prefer the server's pre-stemmed lexemes; raw fields are the fallback for
  // records without a vector (e.g. an exercise created mid-session).
  if (exercise.searchVector) {
    return lexemeScore(word, exercise);
  }
  if (tokenMatches(word, exercise.name)) {
    return NAME_WEIGHT;
  }
  if (exercise.tags.some(({ tag }) => tokenMatches(word, tag.name))) {
    return TAG_WEIGHT;
  }
  if (
    exercise.bodyParts.some(({ bodyPart }) =>
      tokenMatches(word, bodyPart.name),
    ) ||
    exercise.equipment.some(({ equipment }) =>
      tokenMatches(word, equipment.name),
    )
  ) {
    return META_WEIGHT;
  }
  return 0;
}

const byName = <T extends SearchableExercise>(a: T, b: T) =>
  a.name.localeCompare(b.name);

/**
 * Filter + rank a preloaded exercise list for the block builder pickers.
 * Preserves the server's behaviour: AND prefix matching over the weighted
 * fields, alternate-picker group exclusion, and tag-overlap ranking against
 * the prescribed exercise. Does not mutate `exercises`.
 */
export function rankExercises<T extends SearchableExercise>(
  exercises: T[],
  searchTerm: string,
  { baseExerciseId, excludeExerciseIds }: AlternateExerciseSearchParams = {},
  locale = 'en',
): T[] {
  // Alternate-picker only, same coupling as the server: no baseExerciseId
  // means no exclusion and no overlap ranking.
  const candidates =
    baseExerciseId && excludeExerciseIds?.length
      ? exercises.filter(
          (exercise) => !excludeExerciseIds.includes(exercise.id),
        )
      : exercises;

  const baseTagIds = new Set(
    (baseExerciseId
      ? exercises.find((exercise) => exercise.id === baseExerciseId)
      : undefined
    )?.tags.map(({ tag }) => tag.id) ?? [],
  );
  const useOverlapRanking = baseTagIds.size > 0;
  const sharedTagCount = (exercise: T) =>
    exercise.tags.filter(({ tag }) => baseTagIds.has(tag.id)).length;

  const words = searchWords(searchTerm, locale);

  if (words.length === 0) {
    // Symbol-only input produces no tsquery filter server-side (full list),
    // but input that is *only stop words* yields an empty tsquery, which
    // matches nothing — mirror both.
    if (searchTerm.trim().length > 0 && /[\p{L}\p{N}]/u.test(searchTerm)) {
      return [];
    }
    return [...candidates].sort(
      useOverlapRanking
        ? (a, b) => sharedTagCount(b) - sharedTagCount(a) || byName(a, b)
        : byName,
    );
  }

  const scored = candidates
    .map((exercise) => {
      const wordScores = words.map((word) => wordScore(word, exercise));
      // tsquery `&` semantics: every word must match something.
      if (wordScores.some((score) => score === 0)) {
        return { exercise, textScore: 0 };
      }
      const textScore =
        wordScores.reduce((sum, score) => sum + score, 0) / words.length;
      return { exercise, textScore };
    })
    .filter(({ textScore }) => textScore > 0);

  return scored
    .sort((a, b) => {
      const score = (s: { exercise: T; textScore: number }) =>
        useOverlapRanking
          ? OVERLAP_TEXT_WEIGHT * s.textScore +
            OVERLAP_TAG_WEIGHT * (sharedTagCount(s.exercise) / baseTagIds.size)
          : s.textScore;
      return score(b) - score(a) || byName(a.exercise, b.exercise);
    })
    .map(({ exercise }) => exercise);
}
